import { useT } from '../../../i18n/index.ts';
import { Chip, cx, InfoHint } from '../../../ui/index.ts';
import { CellCap, ModeSegment, NumberField, Segment } from './GuardControls.tsx';
import { GuardTripFields } from './GuardTripFields.tsx';
import type { GuardIssue, SkuSignalDraft } from './guardModel.ts';
import type { GuardCount, GuardSignalStats } from './guardStats.ts';
import type { GuardSignalView } from './guardSignals.ts';

const PREFIX = 'guaranteed-guard-sku';

function countText(count: GuardCount | undefined, t: ReturnType<typeof useT>): string {
  if (count === undefined) return '0';
  if (!count.capped) return String(count.n);
  // R1-A7: a full page may hide this signal's older rows: a lower bound, or
  // "unknown" when the page held none of it — never a plain 0.
  return count.n === 0 ? t('guaranteed.guard.stats.unknown') : t('guaranteed.guard.stats.capped', { n: String(count.n) });
}

function SignalStats({
  kind,
  stats,
  statsState,
}: {
  readonly kind: string;
  readonly stats: GuardSignalStats | undefined;
  readonly statsState: 'ready' | 'loading' | 'failed';
}): JSX.Element {
  const t = useT();
  if (statsState === 'failed') {
    return <p className="m-0 mt-2 text-micro text-paper-low">{t('guaranteed.guard.stats.failed')}</p>;
  }
  const cell = (label: Parameters<typeof t>[0], count: GuardCount | undefined, id: string): JSX.Element => (
    <div className="flex flex-col">
      <b className="font-mono text-dense font-medium text-paper" data-testid={`guaranteed-guard-stat-${kind}-${id}`}>
        {statsState === 'loading' ? '…' : countText(count, t)}
      </b>
      <span className="text-micro text-paper-low">{t(label)}</span>
    </div>
  );
  return (
    <div className="mt-2 flex flex-col gap-1">
      <span className="text-micro text-paper-low">{t('guaranteed.guard.stats.window')}</span>
      <div className="flex gap-3">
        {cell('guaranteed.guard.stats.trips', stats?.trips, 'trips')}
        {cell('guaranteed.guard.stats.shadow_trips', stats?.shadowTrips, 'shadow')}
        {cell('guaranteed.guard.stats.releases', stats?.releases, 'releases')}
      </div>
    </div>
  );
}

export function GuardSignalRow({
  view,
  draft,
  fromLegacy,
  issues,
  stats,
  statsState,
  disabled,
  onChange,
  onEnter,
}: {
  readonly view: GuardSignalView;
  readonly draft: SkuSignalDraft;
  readonly fromLegacy: boolean;
  readonly issues: ReadonlyMap<string, GuardIssue>;
  readonly stats: GuardSignalStats | undefined;
  readonly statsState: 'ready' | 'loading' | 'failed';
  readonly disabled: boolean;
  readonly onChange: (next: SkuSignalDraft) => void;
  readonly onEnter?: () => void;
}): JSX.Element {
  const t = useT();
  const { spec } = view;
  const kind = spec.kind;
  const walkOff = draft.walk === 'off';
  return (
    <div
      className="grid grid-cols-[13rem_1fr_1fr_1fr] overflow-hidden rounded-lg border border-line bg-card"
      data-testid={`guaranteed-guard-row-${kind}`}
    >
      <div className="min-w-0 bg-raised p-3">
        <div className="flex flex-wrap items-center gap-1.5 text-section text-paper">
          {t(view.title)}
          {view.tag === null ? null : <Chip tone="neutral" lamp={false}>{t(view.tag)}</Chip>}
        </div>
        <p className="m-0 mt-1.5 text-label text-paper-mid">{t(view.desc)}</p>
        <SignalStats kind={kind} stats={stats} statsState={statsState} />
      </div>

      <div className="min-w-0 border-l border-line-soft p-3">
        <CellCap>
          <span className="inline-flex items-center gap-1">
            {t('guaranteed.guard.col.judge')}
            {kind === 'first_content_stream' ? (
              <InfoHint
                label={t('guaranteed.cutoff.hint_label')}
                content={t('guaranteed.cutoff.hint')}
                testId="guaranteed-cutoff-hint"
              />
            ) : null}
          </span>
        </CellCap>
        {view.thresholdLabel === null ? null : (
          <NumberField
            label={t(view.thresholdLabel)}
            unit="guaranteed.guard.unit.seconds"
            value={draft.threshold}
            issue={issues.get(`${kind}.threshold`)}
            disabled={disabled}
            testId={`${PREFIX}-${kind}-threshold`}
            onChange={(text) => onChange({ ...draft, threshold: text })}
            onEnter={onEnter}
          />
        )}
        {fromLegacy ? (
          <span className="text-micro text-warn" data-testid="guaranteed-guard-legacy-stream">
            {t('guaranteed.guard.legacy')}
          </span>
        ) : null}
        {view.judge === null ? null : <p className="m-0 mt-1 text-label text-paper-low">{t(view.judge)}</p>}
      </div>

      <div className="min-w-0 border-l border-line-soft p-3">
        {view.walkTitle === null ? (
          <div className="flex h-full flex-col justify-center gap-1 text-dense text-paper-low">
            <b className="font-medium text-paper-low">{t('guaranteed.guard.walk_none_title')}</b>
            {t('guaranteed.guard.walk_none_body')}
          </div>
        ) : (
          <>
            <CellCap>
              <span>{t(view.walkTitle)}</span>
              <ModeSegment
                value={draft.walk}
                disabled={disabled}
                ariaLabel={t(view.walkTitle)}
                testId={`${PREFIX}-${kind}-walk`}
                onChange={(walk) => onChange({ ...draft, walk })}
              />
            </CellCap>
            <div className={cx('transition-opacity duration-120', walkOff && 'opacity-50')}>
              {spec.retryAfterCap ? (
                <NumberField
                  label={t('guaranteed.guard.field.retry_after_cap')}
                  unit="guaranteed.guard.unit.seconds"
                  value={draft.cap}
                  placeholder="—"
                  issue={issues.get(`${kind}.cap`)}
                  disabled={disabled}
                  testId={`${PREFIX}-${kind}-cap`}
                  onChange={(text) => onChange({ ...draft, cap: text })}
                  onEnter={onEnter}
                />
              ) : null}
              {spec.retryAfterReasoning ? (
                <div className="flex items-center justify-between gap-2 py-1">
                  <span className="text-label text-paper-mid">{t('guaranteed.guard.field.retry_after_reasoning')}</span>
                  <Segment
                    value={draft.retryAfterReasoning ? 'yes' : 'no'}
                    disabled={disabled}
                    ariaLabel={t('guaranteed.guard.field.retry_after_reasoning')}
                    testId={`${PREFIX}-${kind}-retry`}
                    onChange={(value) => onChange({ ...draft, retryAfterReasoning: value === 'yes' })}
                    options={[
                      { value: 'no', label: t('guaranteed.guard.no') },
                      { value: 'yes', label: t('guaranteed.guard.yes'), tone: 'bg-ok-dim text-ok' },
                    ]}
                  />
                </div>
              ) : null}
              {spec.chatLane ? (
                <div className="flex items-center justify-between gap-2 py-1">
                  <span className="text-label text-paper-mid">{t('guaranteed.guard.field.chat_lane')}</span>
                  <Segment
                    value={draft.chatLane ? 'yes' : 'no'}
                    disabled={disabled}
                    ariaLabel={t('guaranteed.guard.field.chat_lane')}
                    testId={`${PREFIX}-${kind}-chat-lane`}
                    onChange={(value) => onChange({ ...draft, chatLane: value === 'yes' })}
                    options={[
                      { value: 'no', label: t('guaranteed.guard.no') },
                      { value: 'yes', label: t('guaranteed.guard.yes'), tone: 'bg-ok-dim text-ok' },
                    ]}
                  />
                </div>
              ) : null}
              {spec.chatLane ? <p className="m-0 mt-1 text-label text-paper-low">{t('guaranteed.guard.field.chat_lane_note')}</p> : null}
              {view.walkNote === null ? null : <p className="m-0 mt-1 text-label text-paper-low">{t(view.walkNote)}</p>}
            </div>
          </>
        )}
      </div>

      <div className="min-w-0 border-l border-line-soft p-3">
        <CellCap>
          <span>{t(view.tripTitle)}</span>
          <ModeSegment
            value={draft.trip.mode}
            disabled={disabled}
            ariaLabel={t(view.tripTitle)}
            testId={`${PREFIX}-${kind}-trip`}
            onChange={(mode) => onChange({ ...draft, trip: { ...draft.trip, mode } })}
          />
        </CellCap>
        <GuardTripFields
          kind={kind}
          prefix={PREFIX}
          trip={draft.trip}
          issues={issues}
          disabled={disabled}
          onChange={(trip) => onChange({ ...draft, trip })}
          onEnter={onEnter}
        />
        {/* R1-B5: hits come only from this signal's walk (S1–S4: act or shadow;
            stall: act only, the v1 M10 rule), so such a trip can never fire. */}
        {view.walkTitle !== null && draft.trip.mode !== 'off' && (walkOff || (kind === 'stall' && draft.walk === 'shadow')) ? (
          <p className="m-0 mt-2 text-label text-bad" data-testid={`${PREFIX}-${kind}-trip-dead`}>
            {t(kind === 'stall' ? 'guaranteed.guard.trip_without_walk_stall' : 'guaranteed.guard.trip_without_walk')}
          </p>
        ) : null}
        {view.tripNote === null ? null : (
          <p className="m-0 mt-2 rounded border border-signal bg-signal-dim px-2.5 py-2 text-label text-signal">
            {t(view.tripNote)}
          </p>
        )}
      </div>
    </div>
  );
}
